import { useEffect, useState } from 'react';
import apiClient from '../../lib/api/client';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, AlertCircle, TrendingDown, TrendingUp } from 'lucide-react';
import LoginAsCard from './LoginAsCard';

interface FlowMetrics {
  date_from: string;
  date_to: string;
  po_line_value: number;
  po_lines: number;
  schedule_value: number;
  schedule_slots: number;
  demand_value: number;
  grn_value: number;
  grns: number;
  ncrs_created: number;
  /** Raised by a person in Helix. */
  ncrs_by_users: number;
  /** Brought in by an import or ERP push. */
  ncrs_processed: number;
  ncrs_collaborated: number;
  ncrs_closed: number;
  appeals_submitted: number;
  appeals_resolved: number;
  otif_overrides: number;
  collaboration_events: number;
  /** Accepts, declines and proposals a person made themselves. */
  collaboration_by_users: number;
  /** Made under Login-As (Valuechain staff or a customer admin acting as the user). */
  collaboration_login_as: number;
  /** Made by imports, ERP snapshots and the auto-acknowledge timer. */
  collaboration_processed: number;
}

interface RoleTime { hours: number; users: number; sessions: number }
export interface LoginAsStats {
  hours: number;
  users: number;
  sessions: number;
  /** Login-As sessions started in the window (from the audit log). */
  sessions_started: number;
  impersonators: number;
  people_impersonated: number;
  /** Who logged in as whom: by = valuechain | customer, as_role = e.g. supplier_admin. */
  by_pair: { by: string; as_role: string; sessions: number }[];
}

interface TimeWindow {
  roles: Record<string, RoleTime>;
  total_hours: number;
  active_users: number;
  sessions: number;
  avg_hours_per_active_user: number;
  avg_session_minutes: number;
  /** Time spent under Login-As, kept out of every figure above. */
  login_as: LoginAsStats;
}
interface TimeSpent {
  today: TimeWindow;
  last_7_days: TimeWindow;
  last_30_days: TimeWindow;
}

const ROLE_LABELS: Record<string, string> = {
  customer_admin: 'Customer admins',
  customer_user: 'Customer users',
  supplier_admin: 'Supplier admins',
  supplier_user: 'Supplier users',
};

interface StorageArea { files: number; bytes: number; added_this_month: number }
interface StorageStats {
  backend: string;
  total_files: number;
  total_bytes: number;
  added_this_month: number;
  areas: Record<string, StorageArea>;
}

interface ValueKpis {
  period: 'month' | 'quarter' | 'year';
  current: FlowMetrics;
  previous: FlowMetrics;
  people: {
    customer_users: number;
    customer_admins: number;
    supplier_users: number;
    suppliers_total: number;
    /** Supplier companies with at least one active login. */
    suppliers_with_login: number;
    suppliers_trading_this_period: number;
    active_today: number;
    active_this_week: number;
  };
}

const PERIOD_LABELS = { month: 'This month', quarter: 'This quarter', year: 'This year' } as const;
const PREV_LABELS = { month: 'last month', quarter: 'last quarter', year: 'last year' } as const;

function storageSize(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(0)} MB`;
  return `${(bytes / 1_000).toFixed(0)} kB`;
}

function money(v: number): string {
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `£${(v / 1_000).toFixed(0)}k`;
  return `£${v.toFixed(0)}`;
}

/** One big-number KPI tile, screenshot-style: label, sub-context, value, and
 *  an optional vs-previous delta. */
function Tile({ label, sub, value, prev, prevLabel, format = (n: number) => n.toLocaleString() }: {
  label: string;
  sub?: string;
  value: number;
  prev?: number;
  prevLabel?: string;
  format?: (n: number) => string;
}) {
  const delta = prev !== undefined && prev > 0 ? ((value - prev) / prev) * 100 : null;
  return (
    <Card className="border-slate-200">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-700">{label}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
            {delta !== null && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${delta >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {delta >= 0 ? '+' : ''}{delta.toFixed(0)}% vs {prevLabel}
              </p>
            )}
          </div>
          <p className="text-3xl font-semibold tabular-nums shrink-0">{format(value)}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Platform Value tab — "is the platform useful?" in one screen.
 *
 * People on the platform, and the business volume flowing through it for a
 * calendar month / quarter / year, each with the previous period alongside.
 */
export default function PlatformValueTab() {
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');
  const [data, setData] = useState<ValueKpis | null>(null);
  const [time, setTime] = useState<TimeSpent | null>(null);
  const [storage, setStorage] = useState<StorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      apiClient.get<ValueKpis>(`/analytics/platform/value-kpis/?period=${period}`),
      apiClient.get<TimeSpent>('/analytics/platform/time-spent/'),
      apiClient.get<StorageStats>('/analytics/platform/storage/'),
    ])
      .then(([kpiRes, timeRes, storageRes]) => {
        if (cancelled) return;
        setData(kpiRes.data);
        setTime(timeRes.data);
        setStorage(storageRes.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Value KPIs fetch failed:', err);
        setError('Could not load platform value KPIs.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  if (loading && !data) {
    return <div className="h-64 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (error || !data) {
    return (
      <div className="h-64 flex flex-col items-center justify-center gap-2 text-sm text-red-600">
        <AlertCircle className="h-5 w-5" />{error ?? 'No data.'}
      </div>
    );
  }

  const { current: cur, previous: prev, people } = data;
  const prevLabel = PREV_LABELS[data.period];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Business flowing through the platform, {PERIOD_LABELS[data.period].toLowerCase()} so far
          vs the whole of {prevLabel}. People figures are live.
        </p>
        <Select value={period} onValueChange={(v) => setPeriod(v as typeof period)}>
          <SelectTrigger className="w-40" aria-label="Period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">This month</SelectItem>
            <SelectItem value="quarter">This quarter</SelectItem>
            <SelectItem value="year">This year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">People on the platform</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile label="Active users this week" sub={`${people.active_today} of them today — anyone who used the app`} value={people.active_this_week} />
          <Tile label="Customer accounts" sub={`logins at the customer — ${people.customer_admins} are admins`} value={people.customer_users} />
          <Tile label="Supplier accounts" sub={`logins across ${people.suppliers_with_login} supplier companies (${people.suppliers_total} supplier records on file)`} value={people.supplier_users} />
          <Tile label="Suppliers trading" sub={`companies with a PO or receipt ${PERIOD_LABELS[data.period].toLowerCase()}, of ${people.suppliers_total} on file`} value={people.suppliers_trading_this_period} />
        </div>
      </div>

      {time && (
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Time on the platform</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
            <Tile label="Total hours, last 30 days" sub={`${time.last_7_days.total_hours.toLocaleString()}h this week · ${time.today.total_hours}h today`} value={time.last_30_days.total_hours} format={(n) => `${n.toLocaleString()}h`} />
            <Tile label="Per active user" sub="last 30 days" value={time.last_30_days.avg_hours_per_active_user} format={(n) => `${n}h`} />
            <Tile label="Avg session" sub={`${time.last_30_days.sessions.toLocaleString()} sessions in 30 days`} value={time.last_30_days.avg_session_minutes} format={(n) => `${n}m`} />
            <Tile label="Active in 30 days" sub="distinct people with at least one session" value={time.last_30_days.active_users} />
          </div>
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground mb-2">
                Hours by role. Estimated from activity: requests less than 30 minutes apart count as
                one continuous session, so idle reading time is not counted. Background notification
                checks, Helix/Valuechain staff and Login-As are excluded. Login-As, last 30 days:{' '}
                {time.last_30_days.login_as.hours}h of activity across {time.last_30_days.login_as.users} people.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2 text-right">Today</th>
                      <th className="px-3 py-2 text-right">Last 7 days</th>
                      <th className="px-3 py-2 text-right">Last 30 days</th>
                      <th className="px-3 py-2 text-right" title="Distinct users with a session in the last 30 days">Active users (30d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(ROLE_LABELS).map((role) => (
                      <tr key={role} className="border-b border-slate-100">
                        <td className="px-3 py-1.5 font-medium">{ROLE_LABELS[role]}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{time.today.roles[role]?.hours ?? 0}h</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{time.last_7_days.roles[role]?.hours ?? 0}h</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{time.last_30_days.roles[role]?.hours ?? 0}h</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{time.last_30_days.roles[role]?.users ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <LoginAsCard login={time.last_30_days.login_as} />
        </div>
      )}

      <div>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Order value through the platform</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile
            label="Demand value through platform"
            sub={[
              cur.po_lines > 0 ? `${cur.po_lines.toLocaleString()} PO lines issued` : null,
              cur.schedule_slots > 0 ? `${cur.schedule_slots.toLocaleString()} scheduled deliveries due` : null,
            ].filter(Boolean).join(' + ') || 'no demand in this period'}
            value={cur.demand_value}
            prev={prev.demand_value}
            prevLabel={prevLabel}
            format={money}
          />
          <Tile label="Goods received value" sub={`${cur.grns.toLocaleString()} receipts`} value={cur.grn_value} prev={prev.grn_value} prevLabel={prevLabel} format={money} />
          <Tile
            label="Collaboration actions by people"
            sub={`accepts, declines & proposals · plus ${cur.collaboration_processed.toLocaleString()} processed by imports/ERP/auto-acknowledge, ${cur.collaboration_login_as.toLocaleString()} under Login-As`}
            value={cur.collaboration_by_users}
            prev={prev.collaboration_by_users}
            prevLabel={prevLabel}
          />
          <Tile label="OTIF override decisions" value={cur.otif_overrides} prev={prev.otif_overrides} prevLabel={prevLabel} />
        </div>
      </div>

      {storage && (
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Document storage</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
            <Tile label="Total stored" sub={storage.backend} value={storage.total_bytes} format={storageSize} />
            <Tile label="Documents" sub="uploads across all modules — logos & branding shown separately below" value={storage.total_files} />
            <Tile label="Added this month" sub="new uploads since the 1st" value={storage.added_this_month} />
          </div>
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="px-3 py-2">Area</th>
                      <th className="px-3 py-2 text-right">Files</th>
                      <th className="px-3 py-2 text-right">Stored</th>
                      <th className="px-3 py-2 text-right">Added this month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(storage.areas).map(([area, a]) => (
                      <tr key={area} className="border-b border-slate-100">
                        <td className="px-3 py-1.5 font-medium">{area}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{a.files.toLocaleString()}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{a.bytes > 0 ? storageSize(a.bytes) : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{a.added_this_month.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div>
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Quality & disputes</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile label="NCRs raised by people" sub={`plus ${cur.ncrs_processed.toLocaleString()} imported · ${cur.ncrs_collaborated} of all ${cur.ncrs_created.toLocaleString()} with supplier response`} value={cur.ncrs_by_users} prev={prev.ncrs_by_users} prevLabel={prevLabel} />
          <Tile label="NCRs closed" value={cur.ncrs_closed} prev={prev.ncrs_closed} prevLabel={prevLabel} />
          <Tile label="Appeals submitted" sub={`${cur.appeals_resolved} resolved`} value={cur.appeals_submitted} prev={prev.appeals_submitted} prevLabel={prevLabel} />
        </div>
      </div>
    </div>
  );
}
